import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText, Code, Settings, Upload, X, Loader2, CheckCircle, File } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Switch } from '@/components/ui/switch'
import { templateService, type TemplateOData, type TemplateVariableOData } from '@/lib/api/services/templates'
import { useToast } from '@/components/ui/use-toast'
import { FluentCard } from '@/components/layout/page-header'

interface TemplateFormData {
  name: string
  description: string
  category: string
  type: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
  content: string
  variables: TemplateVariableOData[]
  isActive: boolean
}

const ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  Word: ['.docx', '.doc', '.dotx', '.docm'],
  Excel: ['.xlsx', '.xls', '.xltx', '.xlsm'],
  PowerPoint: ['.pptx', '.ppt', '.potx', '.pptm'],
  Outlook: ['.msg', '.eml', '.oft'],
}

export default function EditTemplatePage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [template, setTemplate] = useState<TemplateOData | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    category: '',
    type: 'Word',
    content: '',
    variables: [],
    isActive: false,
  })

  useEffect(() => {
    if (params.id) {
      loadTemplate(params.id as string)
    }
  }, [params.id])

  const loadTemplate = async (id: string) => {
    try {
      setLoading(true)
      const data = await templateService.getTemplate(id)
      setTemplate(data)
      setFormData({
        name: data.name,
        description: data.description || '',
        category: data.category || '',
        type: data.type,
        content: data.content,
        variables: data.variables || [],
        isActive: data.isActive,
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to load template',
        variant: 'destructive',
      })
      navigate('/templates')
    } finally {
      setLoading(false)
    }
  }

  const getAcceptedExtensions = () => {
    return ACCEPTED_FILE_TYPES[formData.type]?.join(',') || '.docx,.doc'
  }

  const isValidFileType = (file: File) => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    return ACCEPTED_FILE_TYPES[formData.type]?.includes(extension)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (isValidFileType(file)) {
        setSelectedFile(file)
      } else {
        toast({
          title: 'Invalid file type',
          description: `Please upload a ${formData.type} file (${ACCEPTED_FILE_TYPES[formData.type]?.join(', ')})`,
          variant: 'destructive',
        })
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (isValidFileType(file)) {
        setSelectedFile(file)
      } else {
        toast({
          title: 'Invalid file type',
          description: `Please upload a ${formData.type} file (${ACCEPTED_FILE_TYPES[formData.type]?.join(', ')})`,
          variant: 'destructive',
        })
      }
    }
  }

  const handleUploadNewVersion = async () => {
    if (!template || !selectedFile) return

    try {
      setUploading(true)
      await templateService.uploadTemplate({
        file: selectedFile,
        name: template.name, // Use the same name to create a new version
        description: formData.description,
        category: formData.category,
        variables: formData.variables,
      })
      toast({
        title: 'Success',
        description: 'New version uploaded successfully',
      })
      setSelectedFile(null)
      // Reload template to get updated version info
      await loadTemplate(template.id)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to upload new version',
        variant: 'destructive',
      })
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!template || !formData.name?.trim()) {
      toast({
        title: 'Error',
        description: 'Template name is required',
        variant: 'destructive',
      })
      return
    }

    try {
      setSaving(true)
      await templateService.updateTemplate(template.id, formData)
      toast({
        title: 'Success',
        description: 'Template updated successfully',
      })
      navigate(`/templates/${template.id}`)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update template',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="space-y-2">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        </div>
        <FluentCard>
          <div className="space-y-4">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-11 w-full bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="h-24 w-full bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="h-11 w-full bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          </div>
        </FluentCard>
      </div>
    )
  }

  if (!template) return null

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to={`/templates/${template.id}`}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Template
      </Link>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Template</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Update template details or upload a new version
        </p>
      </div>

      {/* Upload New Version Section */}
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
            <Upload className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload New Version</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Upload a new file to create version {(template.currentVersionNumber || 1) + 1}
            </p>
          </div>
        </div>

        {/* Current File Info */}
        {template.originalFileName && (
          <div className="mb-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                template.type === 'Word' ? 'bg-blue-100 dark:bg-blue-900/30' :
                template.type === 'Excel' ? 'bg-green-100 dark:bg-green-900/30' :
                template.type === 'PowerPoint' ? 'bg-orange-100 dark:bg-orange-900/30' :
                'bg-gray-100 dark:bg-gray-700'
              }`}>
                <File className={`h-5 w-5 ${
                  template.type === 'Word' ? 'text-blue-600 dark:text-blue-400' :
                  template.type === 'Excel' ? 'text-green-600 dark:text-green-400' :
                  template.type === 'PowerPoint' ? 'text-orange-600 dark:text-orange-400' :
                  'text-gray-400'
                }`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  Current: {template.originalFileName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Version {template.currentVersionNumber || 1}
                  {template.fileSize && ` • ${formatFileSize(template.fileSize)}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Drop Zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-2xl p-8 transition-all ${
            dragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : selectedFile
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={getAcceptedExtensions()}
            onChange={handleFileSelect}
            className="hidden"
          />

          {selectedFile ? (
            <div className="flex flex-col items-center text-center">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30 mb-3">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="font-medium text-gray-900 dark:text-white mb-1">
                {selectedFile.name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {formatFileSize(selectedFile.size)}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleUploadNewVersion}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload as New Version
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center">
              <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-800 mb-3">
                <Upload className="h-8 w-8 text-gray-400" />
              </div>
              <p className="font-medium text-gray-900 dark:text-white mb-1">
                Drag and drop your file here
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                or click to browse • Supported: {ACCEPTED_FILE_TYPES[formData.type]?.join(', ')}
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Upload className="h-4 w-4" />
                Browse Files
              </button>
            </div>
          )}
        </div>
      </FluentCard>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <FluentCard>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Template name and description</p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Template Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="Enter template name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                id="description"
                placeholder="Enter template description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="type" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Template Type
                </label>
                <select
                  id="type"
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value as 'Word' | 'Excel' | 'PowerPoint' | 'Outlook' })
                  }
                >
                  <option value="Word">Word Document</option>
                  <option value="PowerPoint">PowerPoint Presentation</option>
                  <option value="Excel">Excel Spreadsheet</option>
                  <option value="Outlook">Outlook Email</option>
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="category" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Category
                </label>
                <input
                  id="category"
                  type="text"
                  placeholder="e.g., Marketing, Sales, HR"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>
        </FluentCard>

        {/* Status */}
        <FluentCard>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Status</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Control template availability</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Active Status</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Make this template available for use
              </p>
            </div>
            <Switch
              id="active"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isActive: checked })
              }
            />
          </div>
        </FluentCard>

        {/* Template Content - Only show if there's text content */}
        {formData.content && !template.filePath && (
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
                <Code className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Template Content</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">The content of your template</p>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="content" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Content
              </label>
              <textarea
                id="content"
                placeholder="Enter template content..."
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                rows={12}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all resize-none font-mono text-sm"
              />
            </div>
          </FluentCard>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Link
            to={`/templates/${template.id}`}
            className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-gray-900/20 dark:shadow-white/20"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  )
}
